const libPictView = require('pict-view');

const _ViewConfiguration = require('./panes/HeadlightPanels-Pane-View-Templates.json')['HeadlightPanels-Pane-StatusPaneHtml-View'];

/** @typedef {import('./HeadlightPanels-View.js').Pict} Pict */

/**
 * Create an element with classes and, optionally, text. Server values (statuses, action and user names)
 * only ever reach the DOM through here as text, never as markup.
 * @param {string} pTag - The tag name
 * @param {string} [pClassName] - Space-separated classes
 * @param {any} [pText] - Text content; null or undefined leaves the element empty
 * @return {HTMLElement}
 */
function createElement(pTag, pClassName, pText)
{
	const tmpElement = document.createElement(pTag);
	if (pClassName)
	{
		tmpElement.className = pClassName;
	}
	if ((pText !== undefined) && (pText !== null))
	{
		tmpElement.textContent = String(pText);
	}
	return tmpElement;
}

/**
 * A Bulma panel icon: `<span class="panel-icon"><i class="fa fa-fw <icon>"></i></span>`.
 * @param {string} pIconClass - The Font Awesome icon class
 * @return {HTMLElement}
 */
function createPanelIcon(pIconClass)
{
	const tmpIconWrapper = createElement('span', 'panel-icon');
	tmpIconWrapper.appendChild(createElement('i', `fa fa-fw ${pIconClass}`));
	return tmpIconWrapper;
}

class HeadlightPaneStatus extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		const tmpOptions = Object.assign({}, _ViewConfiguration, pOptions);
		super(pFable, tmpOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		// Approval payloads from the last open, so reopening paints immediately instead of waiting on
		// three round-trips. Keyed by document: the same pane serves whatever document is loaded, and
		// showing one document's approvals under another would be worse than a slow open. A successful
		// approval action discards it (refreshApprovalRegions, or the page reload), so it never outlives
		// the user's own change.
		this.ApprovalCache = { IDDocument: null, State: undefined, Users: undefined, History: undefined };
		// True while a cached copy is on screen and the fresh copy is still in flight. Approval actions
		// stay disabled for that window -- painting a stale action live would let the user act on an
		// approval state the server has already moved past.
		this.ApprovalRevalidating = false;
	}

	/**
	 * Read a cached approval payload, discarding the cache if it belongs to a different document.
	 * @param {string} pKey - Which payload (State, Users, History)
	 * @return {any} The cached payload, or undefined when there is nothing usable
	 */
	getCachedApproval(pKey)
	{
		const tmpIDDocument = this.pict.AppData?.DocumentData?.ReportData?.IDDocument ?? null;
		if (this.ApprovalCache.IDDocument !== tmpIDDocument)
		{
			this.ApprovalCache = { IDDocument: tmpIDDocument, State: undefined, Users: undefined, History: undefined };
		}
		return this.ApprovalCache[pKey];
	}

	/**
	 * Store an approval payload against the document it was fetched for.
	 * @param {string} pKey - Which payload (State, Users, History)
	 * @param {any} pValue - The payload to keep
	 * @return {void}
	 */
	setCachedApproval(pKey, pValue)
	{
		this.ApprovalCache.IDDocument = this.pict.AppData?.DocumentData?.ReportData?.IDDocument ?? null;
		this.ApprovalCache[pKey] = pValue;
	}

	/**
	 * Drop the cached approval payloads and repaint whichever status regions are on screen, after an
	 * approval action moved the document without reloading the page. A region that is not rendered
	 * fetches fresh the next time the pane opens.
	 * @return {Promise<void>}
	 */
	async refreshApprovalRegions()
	{
		this.ApprovalCache = { IDDocument: null, State: undefined, Users: undefined, History: undefined };
		this.ApprovalRevalidating = false;
		this.paintStatusHeader();
		const tmpBuilds = [];
		if (document.getElementById('HeadlightPanels-StatusPanel-Approval-Actions-Content'))
		{
			tmpBuilds.push(this.buildApprovalActionButtons());
		}
		if (document.getElementById('HeadlightPanels-StatusPanel-Users-Content'))
		{
			tmpBuilds.push(this.buildApprovalUserList());
		}
		if (document.getElementById('HeadlightPanels-StatusPanel-History-Content'))
		{
			tmpBuilds.push(this.buildApprovalLogs());
		}
		await Promise.all(tmpBuilds);
	}

	/**
	 * Repaint the pane's heading (status name, icon and colour) from the document's current status. The
	 * template draws it once when the pane renders, so a status change without a reload has to repaint it.
	 * @return {void}
	 */
	paintStatusHeader()
	{
		const tmpHeader = document.getElementById('HeadlightPanels-StatusPanel-Status-Header');
		if (!tmpHeader)
		{
			return;
		}
		const tmpStatus = this.pict.AppData?.DocumentData?.ReportData?.Status || '';
		const tmpStyle = this.pict.providers.DocumentProvider.getApprovalStatusStyle(tmpStatus);
		tmpHeader.className = `panel-heading ${tmpStyle.backgroundColor}`;
		const tmpIcon = document.getElementById('HeadlightPanels-StatusPanel-Status-Header-Icon');
		if (tmpIcon)
		{
			tmpIcon.className = `fas fa-fw ${tmpStyle.iconClass}`;
		}
		const tmpTitle = document.getElementById('HeadlightPanels-StatusPanel-Status-Header-Title');
		if (tmpTitle)
		{
			tmpTitle.textContent = tmpStatus;
		}
	}

	onAfterRenderAsync(pCallback)
	{
		this.buildApprovalActionButtons();
		this.buildApprovalUserList();
		this.buildApprovalLogs();

		// the panel opens in this callback, so we need to wait for it to open before we can build the content
		if (pCallback)
		{
			pCallback();
		}
	}

	/**
	 * Replace a status region's contents -- including its loading skeleton -- with a single message.
	 *
	 * Shared by the empty and the failed states so a region never keeps spinning: the pane HTML ships
	 * a `skeleton-block` placeholder per region, and whatever happens it has to be painted over.
	 * @param {HTMLElement} pPanel - The region element to fill
	 * @param {string} pMessage - The message to display
	 * @return {void}
	 */
	renderPanelMessage(pPanel, pMessage)
	{
		const tmpNotification = document.createElement('div');
		tmpNotification.className = 'notification has-text-text-light is-dark m-3';
		tmpNotification.textContent = pMessage;
		pPanel.innerHTML = '';
		pPanel.appendChild(tmpNotification);
	}

	/**
	 * This function is used to build the approval action buttons for the status panel.
	 * @param {function} [pCallback] - The callback function to execute after the action buttons are built
	 * @return {Promise<void>}
	 * @async
	 */
	async buildApprovalActionButtons(pCallback)
	{
		const id = 'HeadlightPanels-StatusPanel-Approval-Actions-Content';
		const panel = document.getElementById(id);
		if (!panel)
		{
			this.log.error('Failed to find panel element', { id });
			return;
		}
		// Paint whatever the last open left us, so a reopen is not a blank wait, then revalidate behind it.
		const tmpCached = this.getCachedApproval('State');
		const tmpHadCache = (tmpCached !== undefined);
		if (tmpHadCache)
		{
			this.ApprovalRevalidating = true;
			this.paintApprovalActionButtons(panel, tmpCached);
		}
		else
		{
			this.renderPanelMessage(panel, 'Loading approval actions…');
		}
		let fetchedData;
		try
		{
			fetchedData = await this.fable.services.HeadlightDocumentService.fetchApprovalState();
		}
		catch (pError)
		{
			// The service rethrows on failure; unhandled, that both leaves this region on its loading
			// skeleton forever and surfaces as an unhandled rejection (the caller does not await us).
			this.log.error(`Failed to load the approval actions: ${pError?.message || pError}`);
			this.ApprovalRevalidating = false;
			if (tmpHadCache)
			{
				// A stale copy the user can act on beats an error message replacing content they can see;
				// re-enable it now that nothing fresher is coming.
				this.syncApprovalActionButtonState();
			}
			else
			{
				this.renderPanelMessage(panel, 'Could not load the approval actions. Reopen the panel to try again.');
			}
			if (pCallback)
			{
				pCallback();
			}
			return;
		}
		this.setCachedApproval('State', fetchedData);
		this.ApprovalRevalidating = false;
		// Only repaint when something actually moved -- an unnecessary rebuild throws away the buttons
		// the user is reaching for.
		if (!tmpHadCache || JSON.stringify(tmpCached) !== JSON.stringify(fetchedData))
		{
			this.paintApprovalActionButtons(panel, fetchedData);
		}
		// The panel is built on demand, so a save that started before it opened has to be reflected here
		// rather than only on the save-latch transition.
		this.syncApprovalActionButtonState();
		// optional callback
		if (pCallback)
		{
			pCallback();
		}
	}

	/**
	 * Paint the approval actions region from a payload. Split out so the cached copy and the freshly
	 * fetched copy go through exactly the same rendering.
	 * @param {HTMLElement} pPanel - The region element to fill
	 * @param {any} pData - The approval state payload
	 * @return {void}
	 */
	paintApprovalActionButtons(pPanel, pData)
	{
		// handle empty message display
		if (!pData?.Actions?.length)
		{
			this.renderPanelMessage(pPanel, 'No approval actions available at this time.');
			return;
		}
		pPanel.innerHTML = '';
		pData.Actions.forEach((item) =>
		{
			const newItem = document.createElement('a');
			newItem.classList.add('panel-block');
			// The marker is what syncApprovalActionButtonState finds; it survives this rebuild.
			newItem.setAttribute('data-approval-action', item);
			newItem.onclick = (event) => this.pict.providers.DocumentProvider.handleApprovalStatusChange(event, item);
			newItem.append(createPanelIcon(this.pict.providers.DocumentProvider.getApprovalStatusStyle(item).iconClass), createElement('span', '', item));
			pPanel.appendChild(newItem);
		});
		this.syncApprovalActionButtonState();
	}

	/**
	 * Disable the approval action buttons while the document is being written.
	 *
	 * An approval action changes the document server-side and usually locks it. Taken while a save is
	 * still in flight, the lock lands first and the resolving save is rejected -- the user's last edits
	 * are silently lost. Auto-save makes that window recur on a timer, so the buttons have to reflect
	 * the save latch rather than relying on the user not being quick.
	 *
	 * Derives entirely from provider state, so it is safe to call before the panel has been built.
	 * @return {void}
	 */
	syncApprovalActionButtonState()
	{
		const panel = document.getElementById('HeadlightPanels-StatusPanel-Approval-Actions-Content');
		if (!panel)
		{
			return;
		}
		const documentProvider = this.pict.providers?.DocumentProvider;
		const isBusy = Boolean(documentProvider?.IsSaving) || Boolean(documentProvider?.isTakingAction) || this.ApprovalRevalidating;
		let busyTitle = 'An approval action is already in progress.';
		if (documentProvider?.IsSaving)
		{
			busyTitle = 'Saving the document; approval actions are available once the save completes.';
		}
		else if (this.ApprovalRevalidating)
		{
			busyTitle = 'Checking the current approval state...';
		}
		panel.querySelectorAll('a[data-approval-action]').forEach((element) =>
		{
			element.classList.toggle('is-disabled', isBusy);
			if (isBusy)
			{
				element.setAttribute('aria-disabled', 'true');
				element.setAttribute('tabindex', '-1');
				element.setAttribute('title', busyTitle);
			}
			else
			{
				element.removeAttribute('aria-disabled');
				element.removeAttribute('tabindex');
				element.removeAttribute('title');
			}
		});
	}

	/**
	 * This function is used to build the approval logs for the document.
	 * @return {Promise<void>}
	 * @async
	 */
	async buildApprovalLogs()
	{
		const id = 'HeadlightPanels-StatusPanel-History-Content';
		const panel = document.getElementById(id);
		if (!panel)
		{
			this.log.error('Failed to find panel element', { id });
			return;
		}
		// Paint the last known history first; it is read-only, so a stale copy is harmless while we check.
		const tmpCached = this.getCachedApproval('History');
		const tmpHadCache = (tmpCached !== undefined);
		if (tmpHadCache)
		{
			this.paintApprovalLogs(panel, tmpCached);
		}
		/** @type {{ CreateDate: string, Status: string, AfterStatus: string }[]} */
		let fetchedData;
		try
		{
			fetchedData = await this.fable.services.HeadlightDocumentService.fetchApprovalHistory();
		}
		catch (pError)
		{
			this.log.error(`Failed to load the approval history: ${pError?.message || pError}`);
			if (!tmpHadCache)
			{
				this.renderPanelMessage(panel, 'Could not load the approval history. Reopen the panel to try again.');
			}
			return;
		}
		this.setCachedApproval('History', fetchedData);
		if (!tmpHadCache || JSON.stringify(tmpCached) !== JSON.stringify(fetchedData))
		{
			this.paintApprovalLogs(panel, fetchedData);
		}
	}

	/**
	 * Paint the approval history region from a payload.
	 * @param {HTMLElement} pPanel - The region element to fill
	 * @param {{ CreateDate: string, Status: string, AfterStatus: string }[]} pData - The history payload
	 * @return {void}
	 */
	paintApprovalLogs(pPanel, pData)
	{
		// handle empty message display
		if (!pData || !Array.isArray(pData) || pData.length === 0)
		{
			this.renderPanelMessage(pPanel, 'No logs available at this time.');
			return;
		}
		// sort by most recent first (on a copy -- the cached payload is shared with the next open)
		const tmpSorted = pData.slice().sort((a, b) => new Date(b.CreateDate).getTime() - new Date(a.CreateDate).getTime());
		pPanel.innerHTML = '';
		tmpSorted.forEach((item) =>
		{
			const newItem = document.createElement('div');
			newItem.classList.add('panel-block');
			// TODO:DOUG: CSS class clean up
			newItem.append(
				createElement('span', 'mr-1 has-text-text-65 has-text-weight-semibold', item.Status),
				createElement('i', 'fa fa-fw fa-arrow-right'),
				createElement('span', 'ml-1 has-text-weight-semibold', item.AfterStatus),
				createElement('span', 'is-size-7 ml-2 has-text-text-75 mt-1', ` on ${ this.fable.Dates.dayJS(item.CreateDate).format('MMMM DD, YYYY - hh:mm a') }`));
			pPanel.appendChild(newItem);
		});
	}

	/**
	 * This function is used to build the list of users that can approve the document.
	 * @return {Promise<void>}
	 * @async
	 */
	async buildApprovalUserList()
	{
		const id = 'HeadlightPanels-StatusPanel-Users-Content';
		const panel = document.getElementById(id);
		if (!panel)
		{
			this.log.error('Failed to find panel element', { id });
			return;
		}
		// Paint the last known approver list first; it is read-only, so a stale copy is harmless.
		const tmpCached = this.getCachedApproval('Users');
		const tmpHadCache = (tmpCached !== undefined);
		if (tmpHadCache)
		{
			this.paintApprovalUserList(panel, tmpCached);
		}
		let fetchedData;
		try
		{
			fetchedData = await this.fable.services.HeadlightDocumentService.fetchApprovalUsersList();
		}
		catch (pError)
		{
			this.log.error(`Failed to load the approval user list: ${pError?.message || pError}`);
			if (!tmpHadCache)
			{
				this.renderPanelMessage(panel, 'Could not load the approver list. Reopen the panel to try again.');
			}
			return;
		}
		this.setCachedApproval('Users', fetchedData);
		if (!tmpHadCache || JSON.stringify(tmpCached) !== JSON.stringify(fetchedData))
		{
			this.paintApprovalUserList(panel, fetchedData);
		}
	}

	/**
	 * Paint the approver list region from a payload.
	 * @param {HTMLElement} pPanel - The region element to fill
	 * @param {any[]} pData - The approver list payload
	 * @return {void}
	 */
	paintApprovalUserList(pPanel, pData)
	{
		// handle empty message display
		if (!pData || !Array.isArray(pData) || pData.length === 0)
		{
			this.renderPanelMessage(pPanel, 'No users available at this time.');
			return;
		}
		pPanel.innerHTML = '';
		pData.forEach((item) =>
		{
			const newItem = document.createElement('div');
			newItem.classList.add('panel-block');
			newItem.append(
				createPanelIcon('fa-user'),
				createElement('span', '', `${ item.NameFirst ?? '' } ${ item.NameLast ?? '' }`.trim()),
				createElement('span', 'has-text-text-65 is-size-7 pl-1', item.Title));
			pPanel.appendChild(newItem);
		});
	}

	/**
	 * This function is used to update the style of the approval status based on the status of the document.
	 * @param {string} pStatus - The status of the document
	 * @return {void}
	 * @example
	 * updateApprovalsStyleState('Draft');
	 * updateApprovalsStyleState('Submit');
	 */
	updateApprovalsStyleState(pStatus)
	{
		const documentStatus = pStatus || this.pict.AppData?.DocumentData?.ReportData?.Status;
		const backgroundColor = this.pict.providers.DocumentProvider.getApprovalStatusStyle(pStatus).backgroundColor;
		const iconClass = this.pict.providers.DocumentProvider.getApprovalStatusStyle(pStatus).iconClass;

		this.pict.fable.log.info(`Updating approval style state based on status: ${documentStatus}`, { iconClass, backgroundColor });
		this.pict.settings.HeadlightPanels.Configuration.NavBar.Brand.iconClass = iconClass;
		this.pict.settings.HeadlightPanels.Configuration.NavBar.Brand.wrapperClass = backgroundColor;
		this.pict.settings.HeadlightPanels.Configuration.CurrentStatus.iconClass = iconClass;
		this.pict.settings.HeadlightPanels.Configuration.CurrentStatus.wrapperClass = backgroundColor;
	}
}

module.exports = HeadlightPaneStatus;
module.exports.default_configuration = _ViewConfiguration;
